import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useDispatch, useSelector } from "react-redux";
import useDynamicConfig from "./services/dynamic_config/useDynamicConfig";
import useNetworkStatus from "./hooks/useNetworkStatus";
import AppRoutes from "./routes/AppRoutes";
import PageOffline from "./pages/others/PageOffline";
import ForceAppModal from "./utils/ForceAppModal";
import ErrorBoundary from "./components/error/ErrorBoundary";
import { setPageNotFound, setScreenWidth, setDarkMode } from "./services/globalSlice";
import { checkStorePage, loadStyle, allLinks } from "./utils/helpers";
import Store from "./pages/customer/store/Store";
import HomeCustomerNew from "./pages/customer/home/HomeCustomerNew";
import PageNotFound from "./pages/others/PageNotFound";
import ScrollToTop from "./components/global/ScrollToTop";
import SessionInitializer from "./components/session/SessionInitializer";
import { lightTheme, darkTheme } from "./config/theme";
import "./assets/scss/layout.scss";
import "./assets/scss/CreateStore.scss";
import "./assets/scss/StoreSeller.scss";
import "./assets/scss/Order.scss";
import "./assets/scss/Auth.scss";
import "./assets/scss/Profile.scss";
import "./assets/scss/Address.scss";
import "./assets/scss/AddRequest.scss";
import "./assets/scss/HomeCustomer.scss";
import "./assets/scss/HomeSeller.scss";
import "./assets/scss/Seller.scss";
import "./assets/scss/Global.scss";
import "./assets/scss/Store.scss";
import "./assets/scss/Chat.scss";
import "./assets/scss/Setting.scss";
import "./assets/scss/Arabic.scss";
import "./assets/scss/Responsive.scss";

// import useInitAuth from "./services/auth/hooks/useInitAuth"; // import the hook

export default function App() {
  // useInitAuth();
  const { isLoading, user } = useSelector((state) => state.auth);
  const { pageNotFound } = useSelector((state) => state.global);
  const { userProfile } = useSelector((state) => state.profile);
  const { categories } = useSelector((state) => state.store);
  const { isOnline } = useNetworkStatus();
  const { loading: configLoading } = useDynamicConfig();
  const [language, setLanguage] = useState(localStorage.getItem("i18nextLng") || "en");
  const [domainComponent, setDomainComponent] = useState(null);
  const location = useLocation();
  const currentPath = location.pathname; // This will be like "/product/123"
  const dispatch = useDispatch();


  useEffect(() => {

    // check service notification is worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          // Service Worker registered successfully
        })
        .catch((error) => {
          // Service Worker registration failed - non-critical error
        });
    }

    // check find page in app
    dispatch(setPageNotFound(false)); // reset page 404

    const firstSegment = location.pathname.split("/")[1];

    if (!allLinks.includes(firstSegment))
      dispatch(setPageNotFound(true));

    // load style arabic or english
    loadStyle(language);

    // set Screen Size
    dispatch(setScreenWidth(window.innerWidth));

    // set Domain Component 
    if (checkStorePage()) {
      setDomainComponent(<Store />);
    } else {
      setDomainComponent(<HomeCustomerNew />);
    }
  }, [dispatch, location.pathname, categories, language]); // Added language dependency

  // Separate useEffect for screen size to prevent unnecessary API calls
  useEffect(() => {
    const handleResize = () => {
      dispatch(setScreenWidth(window.innerWidth));
    };

    // Set initial screen size
    dispatch(setScreenWidth(window.innerWidth));

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dispatch]);

  // Separate useEffect for domain component to prevent unnecessary re-renders
  useEffect(() => {
    if (checkStorePage()) {
      setDomainComponent(<Store />);
    } else {
      setDomainComponent(<HomeCustomerNew />);
    }
  }, [location.pathname]); // Only depend on pathname for domain component

  // Dark mode detection and state
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { darkMode } = useSelector((state) => state.global);

  // Use user preference if set, otherwise use system preference
  const isDarkMode = darkMode !== null ? darkMode : prefersDarkMode;

  // Memoize theme to prevent unnecessary re-renders
  const theme = useMemo(() => isDarkMode ? darkTheme : lightTheme, [isDarkMode]);

  // Apply dark mode class to body for CSS targeting
  useEffect(() => {
    if (isDarkMode) {
      document.body.setAttribute('data-theme', 'dark');
      document.body.classList.add('dark-mode');
    } else {
      document.body.removeAttribute('data-theme');
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  if (isLoading || !user) {
    return (
      <Box className="full-preloader-position">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ForceAppModal path={location.pathname + location.search} />
        {configLoading ? (
          <Box className="full-preloader-position">
            <CircularProgress />
          </Box>
        ) : isLoading ? (
          <Box className="full-preloader-position">
            <CircularProgress />
          </Box>
        ) : isOnline ? (
          <>
            {pageNotFound ? (
              <PageNotFound />
            ) : (
              <Box className="App">
                <div id="recaptcha-container" style={{ padding: "1rem" }}></div>
                <SessionInitializer />
                {isLoading === true && (
                  <CircularProgress className="full-preloader-position" />
                )}
                {isLoading === false && (
                  <>
                    <ScrollToTop />
                    <AppRoutes userProfile={userProfile} domainComponent={domainComponent} />
                  </>
                )}
              </Box>
            )}
          </>
        ) : (
          <PageOffline />
        )}
      </ThemeProvider>
    </ErrorBoundary>
  );
}
